import { effectiveMiningVp, validateMiningRules, type MiningRules } from "@megacorp-seeker/core-gameplay/mining-economy";
import { resourceRules } from "./generatedGameplayRules.js";
import { REFINERY_PURITY_OUTCOMES, refineryPurityOutput } from "./refineryPurity.js";
import type { CrewRawRewardDraft } from "./crewRawRewards.js";
import type { VpResourceBonus } from "./vpSchedule.js";
import type { CrewRoundRewardTarget } from "./crewRewardSchedule.js";

export const CREW_REWARD_PILOT = Object.freeze({ dailyVp: 250, maximumAverageReturnBps: 6000,
  vpPerQualifiedCube: 25, materialUnits: { COMMON: 15, UNCOMMON: 5, RARE: 3, EPIC: 2, LEGENDARY: 1 } });

/** Explicit admin preset, never silently applied to a live week. Uses one slot
 * from each approved tier band; the weekend combines its three target days. */
export function pilotCrewRawRewardDraft(target: CrewRoundRewardTarget, schedule: TuningSchedule): CrewRawRewardDraft {
  const amounts = new Map<VpResourceBonus["resourceId"], bigint>();
  for (const window of target.windows) {
    const bands = window.resources.map(row => {
      const tier = resourceRules(row.resourceId).tier;
      return tier === "COMMON" ? 1 : tier === "UNCOMMON" || tier === "RARE" ? 2 : 3;
    });
    if (bands.length !== 3 || new Set(bands).size !== 3) throw new Error("pilot_needs_one_bonus_per_tier_band");
    for (const row of window.resources) amounts.set(row.resourceId, (amounts.get(row.resourceId) ?? 0n)
      + BigInt(CREW_REWARD_PILOT.materialUnits[resourceRules(row.resourceId).tier]) * 1_000_000n);
  }
  let draft: CrewRawRewardDraft = { roundId: target.earningRoundId,
    minimumVpBaseUnits: String(BigInt(CREW_REWARD_PILOT.dailyVp * target.windows.length) * 1_000_000n),
    resources: [...amounts].map(([resourceId, amount]) => ({ resourceId, amountBaseUnits: amount.toString() })) };
  const value = evaluateCrewRawRewardDraft(draft, schedule);
  const required = (BigInt(value.averageVpBaseUnits) * 10_000n + 5999n) / 6000n;
  const step = 25_000_000n, rounded = (required + step - 1n) / step * step;
  if (rounded > BigInt(draft.minimumVpBaseUnits)) draft = { ...draft, minimumVpBaseUnits: rounded.toString() };
  return draft;
}

export type TuningSchedule = Readonly<{
  scheduleHash?: string;
  miningVpRules?: MiningRules;
  days: readonly Readonly<{ date: string; resources?: readonly VpResourceBonus[] }>[];
}>;

function bonusRates(schedule: TuningSchedule): Map<VpResourceBonus["resourceId"], number> {
  if (!schedule.scheduleHash || schedule.days.length !== 7 || new Set(schedule.days.map(day => day.date)).size !== 7) throw new Error("tuning_needs_published_week");
  const rules = schedule.miningVpRules ? validateMiningRules(schedule.miningVpRules) : null;
  const rates = new Map<VpResourceBonus["resourceId"], number>();
  for (const day of schedule.days) {
    if (!day.resources?.length || day.resources.length > 3 || new Set(day.resources.map(row => row.resourceId)).size !== day.resources.length) throw new Error("tuning_needs_explicit_bonuses");
    for (const row of day.resources) {
      resourceRules(row.resourceId);
      if (!Number.isSafeInteger(row.multiplier) || row.multiplier < 1 || row.multiplier > 100) throw new Error("tuning_invalid_bonus");
      const rate = rules ? effectiveMiningVp(rules.tiers[resourceRules(row.resourceId).tier], row.multiplier, rules) : row.multiplier;
      rates.set(row.resourceId, Math.max(rates.get(row.resourceId) ?? 1, rate));
    }
  }
  return rates;
}

/** Conversion potential, never a promised VP award or a market-value/anti-Sybil guarantee. */
export function evaluateCrewRawRewardDraft(draft: CrewRawRewardDraft, schedule: TuningSchedule) {
  const rates = bonusRates(schedule), threshold = positive(draft.minimumVpBaseUnits);
  if (!draft.resources.length || new Set(draft.resources.map(row => row.resourceId)).size !== draft.resources.length) throw new Error("tuning_invalid_pack");
  const resources = draft.resources.map(row => {
    resourceRules(row.resourceId);
    if (!rates.has(row.resourceId)) throw new Error("tuning_resource_not_featured");
    return { ...row, amount: positive(row.amountBaseUnits), bestMultiplier: rates.get(row.resourceId)! };
  });
  const vp = (bps: number) => resources.reduce((sum, row) => sum + refineryPurityOutput(row.amount, bps) * BigInt(row.bestMultiplier), 0n);
  let previous = 0, weighted = 0n;
  for (const outcome of REFINERY_PURITY_OUTCOMES) {
    weighted += vp(outcome.multiplierBps) * BigInt(outcome.upperExclusive - previous);
    previous = outcome.upperExclusive;
  }
  const atOne = vp(10_000), average = weighted / 10_000n;
  return { oneToOneVpBaseUnits: atOne.toString(), averageVpBaseUnits: average.toString(),
    lowVpBaseUnits: vp(8_500).toString(), normalHighVpBaseUnits: vp(11_000).toString(), jackpotVpBaseUnits: vp(200_000).toString(),
    averageReturnBps: Number(average * 10_000n / threshold),
    resources: resources.map(({ amount, ...row }) => ({ ...row, averageProcessedBaseUnits: (weightedOutput(amount) / 10_000n).toString() })),
  };
}
function weightedOutput(amount: bigint) {
  let previous = 0, total = 0n;
  for (const row of REFINERY_PURITY_OUTCOMES) { total += refineryPurityOutput(amount, row.multiplierBps) * BigInt(row.upperExclusive - previous); previous = row.upperExclusive; }
  return total;
}
function positive(value: string) {
  if (!/^[0-9]+$/.test(value) || BigInt(value) <= 0n || BigInt(value) > (1n << 64n) - 1n) throw new Error("tuning_invalid_amount");
  return BigInt(value);
}
